import { useState, useEffect, useCallback, useMemo } from 'react'
import { confirm } from 'devextreme/ui/dialog'
import dxTreeList from 'devextreme/ui/tree_list'
import type {
  CalendarRoom,
  CalendarTreeReorderEvent,
  CustomGroupsConfig,
  CustomGroup,
  FlatCalendarNode,
} from '@/routes/calendar/CalendarPage.types'
import {
  buildCustomFlatCalendars,
  CUSTOM_UNASSIGNED_ID,
  flattenCalendarsGrouped,
  normalizeCustomGroupsConfig,
} from '@/routes/calendar/CalendarPage.utils'
import { user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useCalendarStore } from '@/routes/calendar/CalendarPage.store'

// ─── useAutoRefresh ───────────────────────────────────────────────────────────
// gestisce il reload automatico dello scheduler

export const useAutoRefresh = (reloadCalendar: () => void) => {
  const [autoRefreshMinutes, setAutoRefreshMinutes] = useState<number>(() =>
    Number(localStorage.getItem('calendar-auto-refresh-minutes') ?? 0),
  )

  useEffect(() => {
    if (autoRefreshMinutes <= 0) return
    const id = setInterval(
      () => {
        reloadCalendar()
      },
      autoRefreshMinutes * 60 * 1000,
    )
    return () => clearInterval(id)
  }, [autoRefreshMinutes, reloadCalendar])

  const handleAutoRefreshChange = useCallback((minutes: number) => {
    setAutoRefreshMinutes(minutes)
    localStorage.setItem('calendar-auto-refresh-minutes', String(minutes))
  }, [])

  return { autoRefreshMinutes, handleAutoRefreshChange }
}

// ─── useCalendarColors ────────────────────────────────────────────────────────
// gestisce i colori dei calendari

export const useCalendarColors = (users: ((user | CalendarRoom) & { color?: string })[]) => {
  const { colorOverrides } = useCalendarStore()

  const usersWithColors = useMemo(
    () => users.map((u) => ({ ...u, color: colorOverrides[u.email ?? ''] ?? u.color })),
    [users, colorOverrides],
  )

  return { usersWithColors }
}

// ─── useCalendarTree ──────────────────────────────────────────────────────────
// gestisce gli stati del treelist

type CalendarsGrouped = { fullname: string; email: string | null; items: any[] }[]

export const useCalendarTree = (calendarsGrouped: CalendarsGrouped) => {
  const [flatCalendars, setFlatCalendars] = useState<FlatCalendarNode[]>(() => {
    const flat = flattenCalendarsGrouped(calendarsGrouped)
    const savedOrder = localStorage.getItem('calendar-treelist-order')
    if (!savedOrder) return flat
    const order: string[] = JSON.parse(savedOrder)
    return [...flat].sort((a, b) => {
      const ai = order.indexOf(a.id)
      const bi = order.indexOf(b.id)
      if (ai === -1 && bi === -1) return 0
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  })

  const { customMode } = useCalendarStore()

  const persistCustomConfig = useCallback((config: CustomGroupsConfig) => {
    const normalized = normalizeCustomGroupsConfig(config)
    setCustomConfig(normalized)
    localStorage.setItem('calendar-custom-groups', JSON.stringify(normalized))
    return normalized
  }, [])

  const [customConfig, setCustomConfig] = useState<CustomGroupsConfig>(() => {
    const saved = localStorage.getItem('calendar-custom-groups')
    return saved ? normalizeCustomGroupsConfig(JSON.parse(saved)) : { groups: [], assignments: {} }
  })

  const [customOrder, setCustomOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem('calendar-treelist-order-custom')
    return saved ? JSON.parse(saved) : []
  })

  const allLeaves = useMemo(
    () => flattenCalendarsGrouped(calendarsGrouped).filter((n) => n.email !== null),
    [calendarsGrouped],
  )

  const displayedFlatCalendars = useMemo(() => {
    if (!customMode) return flatCalendars
    const base = buildCustomFlatCalendars(allLeaves, customConfig)
    if (!customOrder.length) return base
    return [...base].sort((a, b) => {
      const ai = customOrder.indexOf(a.id)
      const bi = customOrder.indexOf(b.id)
      if (ai === -1 && bi === -1) return 0
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  }, [customMode, allLeaves, customConfig, flatCalendars, customOrder])

  const getDescendantGroupIds = useCallback(
    (groupId: string) => {
      const descendantIds = new Set<string>()
      const visit = (parentId: string) => {
        for (const group of customConfig.groups.filter((candidate) => candidate.parentId === parentId)) {
          if (descendantIds.has(group.id)) continue
          descendantIds.add(group.id)
          visit(group.id)
        }
      }
      visit(groupId)
      return descendantIds
    },
    [customConfig.groups],
  )

  const handleGroupCreate = useCallback(
    (name: string, parentGroupId?: string) => {
      const parentId = parentGroupId ?? null
      if (parentId && !customConfig.groups.some((group) => group.id === parentId)) return
      const newGroup: CustomGroup = { id: `custom_${Date.now()}`, name, parentId }
      persistCustomConfig({ ...customConfig, groups: [...customConfig.groups, newGroup] })
    },
    [customConfig, persistCustomConfig],
  )

  const handleGroupRename = useCallback(
    (groupId: string, name: string) => {
      const newConfig = {
        ...customConfig,
        groups: customConfig.groups.map((g) => (g.id === groupId ? { ...g, name } : g)),
      }
      persistCustomConfig(newConfig)
    },
    [customConfig, persistCustomConfig],
  )

  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      const targetGroup = customConfig.groups.find((group) => group.id === groupId)
      if (!targetGroup) return

      const ok = await confirm(
        targetGroup.parentId === null
          ? 'Eliminare il gruppo? Tutti i gruppi figli verranno rimossi e i calendari spostati in "Senza gruppo".'
          : 'Eliminare il gruppo? Tutti i gruppi figli verranno rimossi e i calendari spostati nel gruppo padre.',
        'Conferma',
      )
      if (!ok) return

      const deletedIds = new Set([groupId, ...getDescendantGroupIds(groupId)])

      const fallbackGroupId = targetGroup.parentId ?? CUSTOM_UNASSIGNED_ID
      const newAssignments = Object.fromEntries(
        Object.entries(customConfig.assignments).map(([leafId, assignedGroupId]) => [
          leafId,
          deletedIds.has(assignedGroupId) ? fallbackGroupId : assignedGroupId,
        ]),
      )
      const newConfig = {
        groups: customConfig.groups.filter((group) => !deletedIds.has(group.id)),
        assignments: newAssignments,
      }
      persistCustomConfig(newConfig)
    },
    [customConfig, getDescendantGroupIds, persistCustomConfig],
  )

  const handleTreeReorder = useCallback(
    (e: CalendarTreeReorderEvent) => {
      const cancelReorder = () => {
        ;(e as any).cancel = true
      }

      if (customMode && e.dropInsideItem) {
        const treeList: dxTreeList = e.component as dxTreeList
        const visibleRows = treeList.getVisibleRows()
        const targetRow = visibleRows[e.toIndex]
        if (!targetRow) {
          cancelReorder()
          return
        }

        const draggedNode = e.itemData as FlatCalendarNode
        const targetGroup = targetRow.data as FlatCalendarNode

        if (targetGroup.nodeType === 'leaf') {
          cancelReorder()
          return
        }

        if (draggedNode.nodeType === 'leaf') {
          if (!['group', 'unassigned'].includes(targetGroup.nodeType ?? '')) {
            cancelReorder()
            return
          }
          persistCustomConfig({
            ...customConfig,
            assignments: { ...customConfig.assignments, [draggedNode.id]: targetGroup.id },
          })
          return
        }

        if (draggedNode.nodeType !== 'group' || targetGroup.nodeType !== 'group') {
          if (draggedNode.nodeType === 'group' && targetGroup.nodeType === 'unassigned') {
            persistCustomConfig({
              ...customConfig,
              groups: customConfig.groups.map((group) =>
                group.id === draggedNode.id ? { ...group, parentId: null } : group,
              ),
            })
            return
          }

          cancelReorder()
          return
        }

        if (draggedNode.id === targetGroup.id || getDescendantGroupIds(draggedNode.id).has(targetGroup.id)) {
          cancelReorder()
          return
        }

        const newConfig = {
          ...customConfig,
          groups: customConfig.groups.map((group) =>
            group.id === draggedNode.id ? { ...group, parentId: targetGroup.id } : group,
          ),
        }
        persistCustomConfig(newConfig)
        return
      }
      if (customMode) {
        const treeListC: dxTreeList = e.component as dxTreeList
        const visibleRowsC = treeListC.getVisibleRows()
        const draggedIdC = (e.itemData as FlatCalendarNode).id
        const targetRowC = visibleRowsC[e.toIndex]
        if (!targetRowC) {
          cancelReorder()
          return
        }
        const draggedNodeC = e.itemData as FlatCalendarNode
        const targetIdC = (targetRowC.data as FlatCalendarNode).id
        const targetNodeC = targetRowC.data as FlatCalendarNode

        let nextCustomConfig = customConfig
        if (draggedNodeC.nodeType === 'group') {
          const descendantGroupIds = getDescendantGroupIds(draggedNodeC.id)
          const nextParentId =
            targetNodeC.nodeType === 'group'
              ? targetNodeC.parentId
              : targetNodeC.parentId && targetNodeC.parentId !== CUSTOM_UNASSIGNED_ID
                ? targetNodeC.parentId
                : null

          if (nextParentId === draggedNodeC.id || (nextParentId !== null && descendantGroupIds.has(nextParentId))) {
            cancelReorder()
            return
          }

          nextCustomConfig = {
            ...customConfig,
            groups: customConfig.groups.map((group) =>
              group.id === draggedNodeC.id ? { ...group, parentId: nextParentId } : group,
            ),
          }
        }

        const fromIdxC = displayedFlatCalendars.findIndex((n) => n.id === draggedIdC)
        const toIdxC = displayedFlatCalendars.findIndex((n) => n.id === targetIdC)
        if (fromIdxC === -1 || toIdxC === -1) return
        const newDataC = [...displayedFlatCalendars]
        newDataC.splice(toIdxC, 0, newDataC.splice(fromIdxC, 1)[0])
        const newOrderC = newDataC.map((n) => n.id)
        if (nextCustomConfig !== customConfig) persistCustomConfig(nextCustomConfig)
        setCustomOrder(newOrderC)
        localStorage.setItem('calendar-treelist-order-custom', JSON.stringify(newOrderC))
        return
      }
      const treeList: dxTreeList = e.component as dxTreeList
      const visibleRows = treeList.getVisibleRows()
      const draggedId = (e.itemData as FlatCalendarNode).id
      const targetRow = visibleRows[e.toIndex]
      if (!targetRow) return
      const targetId = (targetRow.data as FlatCalendarNode).id

      const fromIdx = flatCalendars.findIndex((n) => n.id === draggedId)
      const toIdx = flatCalendars.findIndex((n) => n.id === targetId)
      if (fromIdx === -1 || toIdx === -1) return

      const newData = [...flatCalendars]
      newData.splice(toIdx, 0, newData.splice(fromIdx, 1)[0])
      setFlatCalendars(newData)
      localStorage.setItem('calendar-treelist-order', JSON.stringify(newData.map((n) => n.id)))
    },
    [customMode, customConfig, displayedFlatCalendars, flatCalendars, getDescendantGroupIds, persistCustomConfig],
  )

  return {
    displayedFlatCalendars,
    handleGroupCreate,
    handleGroupRename,
    handleDeleteGroup,
    handleTreeReorder,
  }
}
